import { useQuery } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Answer, RefusalCard } from '@/components/common/Answer';
import { AsOfStrip } from '@/components/common/AsOfStrip';
import { ExplainerCard } from '@/components/common/ExplainerCard';
import { ExportButton } from '@/components/common/ExportButton';
import { FieldGuide } from '@/components/common/FieldGuide';
import { ErrorMessage, SkeletonLoader } from '@/components/common/StateViews';
import { FineDistributionPanel } from '@/pages/anatel/FineDistributionPanel';
import { OutcomeMixPanel } from '@/pages/anatel/OutcomeMixPanel';
import { OutcomeTrendPanel, type TrendSeries } from '@/pages/anatel/OutcomeTrendPanel';
import { ArchiveSources, WindowCaption } from '@/pages/cade/WindowCaption';
import { fetchCadeOverview } from '@/services/cade';
import type { CadeArchiveYear } from '@/services/types';

/**
 * The three outcomes a practitioner tracks year over year, and the remainder.
 * Mergers stay in the table above: approvals are a different docket from the
 * conduct cases these describe. The unclassified series is dashed and muted so
 * the classified shares are never read as the whole of a year.
 */
const TREND_SERIES: TrendSeries[] = [
  { code: 'TCC_HOMOLOGATED', labelKey: 'cade:overview.outcomes.TCC_HOMOLOGATED', stroke: 'hsl(var(--primary))' },
  { code: 'CONDEMNED_FINED', labelKey: 'cade:overview.outcomes.CONDEMNED_FINED', stroke: 'hsl(var(--destructive))' },
  { code: 'ARCHIVED', labelKey: 'cade:overview.outcomes.ARCHIVED', stroke: 'hsl(var(--foreground))' },
  {
    code: 'UNCLASSIFIED',
    labelKey: 'cade:overview.outcomes.UNCLASSIFIED',
    stroke: 'hsl(var(--muted-foreground))',
    dashed: true,
  },
];

/**
 * The archive per filing year: counts over final decisions, one row per year.
 *
 * A table and not a chart, deliberately. A year holds between 28 and 111 final
 * decisions, so a line through these points would read as a trend in the
 * tribunal's behaviour when it mostly reads how many sessions were held; the
 * fine column is worse -- one cartel settlement moves a year by hundreds of
 * millions. The N sits beside every figure it qualifies, which a chart cannot
 * do, and the fine column is over FINAL decisions only (a deferred session
 * repeats the same amounts), which the help line beneath the table says.
 */
function ArchiveYearTable({ rows }: { rows: CadeArchiveYear[] }) {
  const { t, i18n } = useTranslation(['cade']);
  const number = new Intl.NumberFormat(i18n.language);
  const currency = new Intl.NumberFormat(i18n.language, { style: 'currency', currency: 'BRL' });
  const columns = [
    'sessions',
    'finalDecisions',
    'classified',
    'tcc',
    'condemnedFined',
    'archived',
    'merger',
    'byMajority',
    'fineRows',
    'finesBrl',
  ] as const;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-muted/50 text-left text-xs text-muted-foreground">
          <tr>
            <th className="px-3 py-2 font-medium">{t('cade:overview.archiveByYear.columns.year')}</th>
            {columns.map((column) => (
              <th key={column} className="px-3 py-2 text-right font-medium">
                {t(`cade:overview.archiveByYear.columns.${column}`)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.year} className="border-t hover:bg-muted/30">
              <td className="px-3 py-2 tabular-nums text-foreground">{row.year}</td>
              <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                {number.format(row.sessions)}
              </td>
              <td className="px-3 py-2 text-right tabular-nums font-medium text-foreground">
                {number.format(row.final_decisions)}
              </td>
              <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                {number.format(row.classified)}
              </td>
              <td className="px-3 py-2 text-right tabular-nums text-foreground">
                {number.format(row.tcc)}
              </td>
              <td className="px-3 py-2 text-right tabular-nums text-foreground">
                {number.format(row.condemned_fined)}
              </td>
              <td className="px-3 py-2 text-right tabular-nums text-foreground">
                {number.format(row.archived)}
              </td>
              <td className="px-3 py-2 text-right tabular-nums text-foreground">
                {number.format(row.merger)}
              </td>
              <td className="px-3 py-2 text-right tabular-nums text-foreground">
                {number.format(row.by_majority)}
              </td>
              {/* The N that makes the money column readable sits immediately
                  left of the amount, as on /cade/activity. */}
              <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                {number.format(row.fine_rows)}
              </td>
              <td className="px-3 py-2 text-right tabular-nums text-foreground">
                {row.fines_brl > 0 ? currency.format(row.fines_brl) : ''}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * CADE, and the analyses this corpus will not pretend to support.
 *
 * The third agency and the most refusal-heavy screen in the product: three
 * panels report, three decline. That is the intended output.
 *
 * The decision-maker tables here are the ones ANATEL structurally lacks —
 * including the rapporteur-follow rate that is the whole reason vote data is
 * worth collecting. They landed recently and they are populated, which is
 * precisely what makes them dangerous: a median of two opportunities admits only
 * 0%, 50% and 100%, so a ranking would look authoritative and reshuffle on the
 * next vote recorded. The refusals carry those denominators, so a reader can see
 * this is a question of volume rather than of the analysis being unsound.
 *
 * The consistency refusal is a different kind. Its comparator keys on shared
 * outcome codes, so a disagreeing pair cannot be formed at all — more data will
 * never help. Distinguishing "too little yet" from "never, by construction" is
 * the substrate contract doing the job it exists for.
 */
export function CadeOverviewPage() {
  const { t, i18n } = useTranslation(['cade', 'common']);
  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['cade', 'overview'],
    queryFn: fetchCadeOverview,
  });

  const number = new Intl.NumberFormat(i18n.language);

  if (isPending) {
    return (
      <div className="space-y-4 p-6">
        <SkeletonLoader rows={2} />
        <SkeletonLoader rows={6} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <ErrorMessage message={error.message} onRetry={() => void refetch()} />
      </div>
    );
  }

  // Each tile names what it counts AND, on its second line, the nearest
  // figure a reader would otherwise mistake it for. The first tile counted
  // `corpus.cases` (every row in `cases`, 8,123) until 2026-09-14, directly
  // under a guide saying it was NOT the published register -- the fixture
  // used 464 for it, so the contradiction never reached a test.
  const stats: { key: string; value: number; hint: ReactNode }[] = [
    {
      key: 'adjudicatedCases',
      value: data.corpus.adjudicated_cases,
      hint: (
        <Link to="/cade/activity" className="underline underline-offset-2 hover:text-foreground">
          {t('cade:overview.corpus.registryHint', { count: data.corpus.cases })}
        </Link>
      ),
    },
    {
      key: 'decisions',
      value: data.corpus.decisions,
      hint: t('cade:overview.corpus.finalHint', { count: data.corpus.final_decisions }),
    },
    {
      key: 'votes',
      value: data.corpus.votes,
      hint: t('cade:overview.corpus.canonicalHint', { count: data.corpus.votes_canonical }),
    },
    {
      key: 'commissioners',
      value: data.corpus.commissioners,
      hint: t('cade:overview.corpus.sessionsHint', { count: data.corpus.sessions }),
    },
  ];

  return (
    <div className="space-y-4 p-6">
      <header className="space-y-1">
        <h1 className="text-lg font-semibold text-foreground">{t('cade:overview.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('cade:overview.subtitle')}</p>
      </header>

      <ExplainerCard i18nKey="cade:overview.explainer" />

      <AsOfStrip asOf={data.as_of} />

      <div className="flex justify-end">
        <ExportButton path="/api/v1/cade/export/overview" />
      </div>

      <div className="space-y-2">
        <dl data-corpus-tiles className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {stats.map((stat) => (
            <div key={stat.key} className="rounded-lg border bg-card p-3">
              <dt className="text-xs text-muted-foreground">
                {t(`cade:overview.corpus.${stat.key}`)}
              </dt>
              <dd className="mt-1 text-xl font-semibold tabular-nums text-foreground">
                {number.format(stat.value)}
              </dd>
              <dd className="mt-1 text-xs text-muted-foreground">{stat.hint}</dd>
            </div>
          ))}
        </dl>
        {/* Once for the group: every tile above counts rows of the same
            archive, so the window is one fact, not four. */}
        <WindowCaption window={data.as_of.year_window} />
        <ArchiveSources sources={data.archive_sources} />
      </div>

      {/* level 2 because the stats grid above it carries no heading of its own,
          so an h3 here would jump straight from the page h1. */}
      <FieldGuide i18nKey="cade:overview.guide.corpus" level={2} />

      <section className="space-y-3">
        <h2 className="text-sm font-semibold text-foreground">
          {t('cade:overview.archiveByYear.title')}
        </h2>
        <Answer answer={data.archive_by_year} help={t('cade:overview.archiveByYear.help')}>
          {(rows) => <ArchiveYearTable rows={rows} />}
        </Answer>
        <FieldGuide i18nKey="cade:overview.guide.archiveByYear" />
      </section>

      {/* The same years as shares, with each year's final decisions drawn as
          bars behind the lines: 28 to 111 a year, so one decision moves a
          share by up to four points and the denominator is the reading. The
          shared ANATEL chart, under this page's title and codes. */}
      <section className="space-y-3" data-outcome-trend>
        <OutcomeTrendPanel
          answer={data.outcome_trend}
          titleKey="cade:overview.trend.title"
          helpKey="cade:overview.trend.help"
          volumeLabelKey="cade:overview.trend.volume"
          series={TREND_SERIES}
        />
        <WindowCaption window={data.as_of.year_window} />
        <FieldGuide i18nKey="cade:overview.guide.trend" />
      </section>

      <div className="grid gap-4 lg:grid-cols-2">
        <OutcomeMixPanel
          answer={data.outcome_mix}
          titleKey="cade:overview.outcomeMix.title"
          helpKey="cade:overview.outcomeMix.help"
          labelPrefix="cade:overview.outcomes."
        />
        <FineDistributionPanel
          answer={data.fine_distribution}
          titleKey="cade:overview.fines.title"
          helpKey="cade:overview.fines.help"
        />
      </div>

      <FieldGuide i18nKey="cade:overview.guide.distributions" />

      {/* `tabIndex={-1}` so the jump can move FOCUS here, not just the
          scrollbar -- a screen-reader user who follows the pointer otherwise
          stays parked on the link and hears nothing new. -1 keeps it out of the
          tab order, so it costs a sighted keyboard user nothing. */}
      <div id="cade-vote-positions" tabIndex={-1} className="scroll-mt-4">
        <OutcomeMixPanel
          answer={data.vote_positions}
          titleKey="cade:overview.votes.title"
          helpKey="cade:overview.votes.help"
          labelPrefix="cade:overview.positions."
        />
      </div>

      <FieldGuide i18nKey="cade:overview.guide.votes" />
      <p className="text-xs text-muted-foreground">
        <Link to="/cade/conselho" className="text-primary underline-offset-4 hover:underline">
          {t('cade:overview.conselhoPointer')}
        </Link>
      </p>

      {/* The refusals, given the same weight as the figures rather than tucked
          away. "This cannot be answered, and here is the denominator" is a
          finding a lawyer can act on; a missing panel is not. */}
      <section className="space-y-3">
        <h2 className="text-sm font-semibold text-foreground">
          {t('cade:overview.refusals.title')}
        </h2>
        <p className="text-xs text-muted-foreground">{t('cade:overview.refusals.intro')}</p>

        <div className="grid gap-4 lg:grid-cols-3">
          {/* Each pointer names what the reader CAN see, never the refused
              analysis under another name. Neither the agreement matrix nor the
              follow rate exists anywhere in this product -- what exists is the
              substrate each would have been derived from, and saying so is the
              difference between a useful link and a false promise.

              The agreement pointer is an in-page anchor: the votes it would rest
              on are counted higher up this same screen. */}
          <RefusalCard
            title={t('cade:overview.refusals.agreement')}
            answer={data.agreement_matrix}
            elsewhere={
              <a
                href="#cade-vote-positions"
                onClick={(event) => {
                  // A bare fragment link only scrolls when the hash CHANGES.
                  // Click it a second time, or click it while the URL already
                  // carries the fragment, and the browser does nothing at all --
                  // which reads as a dead link rather than as "you are already
                  // there". Compounded here because `SidebarLayout` scrolls an
                  // inner `<main>` rather than the document, so there is no page
                  // scroll position for the reader to recognise as unchanged.
                  //
                  // Scrolling explicitly makes every click behave the same. The
                  // `href` stays for middle-click, copy-link and the status bar.
                  event.preventDefault();
                  const panel = document.getElementById('cade-vote-positions');
                  panel?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  panel?.focus({ preventScroll: true });
                }}
                className="text-primary underline-offset-4 hover:underline"
              >
                {t('cade:overview.refusals.agreementVotes')}
              </a>
            }
          />
          <RefusalCard
            title={t('cade:overview.refusals.followRate')}
            answer={data.rapporteur_follow_rate}
            elsewhere={
              <Link
                to="/cade/commissioners"
                className="text-primary underline-offset-4 hover:underline"
              >
                {t('cade:overview.refusals.followRateCaseloads')}
              </Link>
            }
          />
          {/* This one is refused as a corpus-level FIGURE and has a whole screen
              behind it: the 143 surviving pairs are reviewable one at a time at
              `/cade/consistency`. Without the pointer the card reads as "this
              product cannot do consistency review for CADE", which is the
              opposite of what the funnel beside it measured. */}
          <RefusalCard
            title={t('cade:overview.refusals.consistency')}
            answer={data.consistency_review}
            elsewhere={
              <Link to="/cade/consistency" className="text-primary underline-offset-4 hover:underline">
                {t('cade:overview.refusals.consistencyQueue')}
              </Link>
            }
          />
        </div>

        <FieldGuide i18nKey="cade:overview.guide.refusals" />
      </section>

      <section className="rounded-lg border bg-card p-4">
        <h2 id="cade-case-types" className="mb-3 text-sm font-semibold text-foreground">
          {t('cade:overview.caseTypes.title')}
        </h2>
        {/* Named by its own heading. The vocabulary guide below defines the same
            labels, so an unnamed list leaves both a screen-reader user and a
            text-based assertion unable to tell the counted rows from the
            definitions of what they are counting. */}
        <ul aria-labelledby="cade-case-types" className="space-y-1.5 text-sm">
          {data.case_types.map((type) => (
            <li key={type.case_type} className="flex items-baseline justify-between gap-4">
              {/* A link only when the type has judged cases behind it.
                  `/cade/cases` offers the ADJUDICATED vocabulary in its filter,
                  and the registry spells the same concepts differently
                  ("Finalistico: Requerimento de TCC" against "Requerimento de
                  TCC"), so linking a registry label would seed a filter value
                  that cannot match by construction -- the 2026-09-09 defect
                  where 9 of 24 offered types returned nothing. Registry rows
                  stay as text. */}
              {type.adjudicated > 0 ? (
                <Link
                  to={`/cade/cases?type=${encodeURIComponent(type.case_type)}`}
                  className="truncate text-primary underline-offset-4 hover:underline"
                >
                  {type.case_type}
                </Link>
              ) : (
                <span className="truncate text-foreground">{type.case_type}</span>
              )}
              {/* Both numbers. The list spans two populations that name the same
                  concepts differently, so the total alone ranks a registry label
                  above a judged one without saying they do not compare. */}
              <span className="shrink-0 tabular-nums text-muted-foreground">
                {t('cade:overview.caseTypes.counts', {
                  cases: number.format(type.cases),
                  adjudicated: number.format(type.adjudicated),
                })}
              </span>
            </li>
          ))}
        </ul>

        {/* The denominator names the population the first number counts: every
            published row, registry stubs included. `/cade/activity` shows the
            same labels over a DIFFERENT source (the Qlik register), so the same
            type legitimately carries a different count there. */}
        <p className="mt-2 text-xs text-muted-foreground">
          {t('cade:overview.caseTypes.denominator', {
            cases: number.format(data.corpus.cases),
          })}
        </p>

        {/* A `limit` that hides rows silently is the same defect as a filter
            offering rows that do not exist. */}
        {data.case_types_distinct > data.case_types.length ? (
          <p className="mt-2 text-xs text-muted-foreground">
            {t('cade:overview.caseTypes.truncated', {
              shown: number.format(data.case_types.length),
              total: number.format(data.case_types_distinct),
            })}
          </p>
        ) : null}

        <div className="mt-3">
          <FieldGuide i18nKey="cade:overview.guide.caseTypes" />
        </div>

        {/* What the column counts, then what the labels themselves mean. The
            guide above defines the figures; this defines the vocabulary they
            are keyed on. */}
        <div className="mt-3">
          <FieldGuide i18nKey="cade:vocabulary.caseTypes" />
        </div>
      </section>
    </div>
  );
}

/**
 * A refusal rendered as content.
 *
 * `Answer` already declines to draw a value when the substrate is absent; this
 * gives that decline a heading and a place on the page, so a reader sees "we
 * looked, and here is exactly why this cannot be shown" rather than an absence
 * they have to notice for themselves.
 */
